import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Check } from 'lucide-react';
import moment from 'moment';

import { useAppDispatch, useAppSelector } from '../../hooks.ts';
import { subscriptionReducer } from '../../store/reducers/subscription.reducer.ts';
import { getUserFeatureFlags } from '../../store/selectors/featureFlags.selector.ts';
import {
  getBillingCatalog,
  getIsLoadingSubscription,
  getIsOpeningPaymentPage,
  getIsSwitchingToFree,
  getSubscription,
  getSubscriptionError,
} from '../../store/selectors/subscription.selector.ts';
import { PaidTier, SubscriptionTier } from '../../types/subscription.ts';
import { formatPrice } from '../../utils/billing.ts';
import { ONLINE_PAYMENTS_FLAG } from '../../utils/featureFlagKeys.ts';

const {
  loadSubscription,
  loadBillingCatalog,
  startSubscriptionCheckout,
  startCreditPackCheckout,
  openBillingPortal,
  switchToFree,
} = subscriptionReducer.actions;

interface PlanCard {
  name: string;
  tier: SubscriptionTier | 'ENTERPRISE';
  price: string;
  originalPrice?: string;
  period: string;
  /** Small line under the price (the yearly total). */
  priceNote?: string;
  freeText: string;
  /** No button is shown without a text. */
  buttonText?: string;
  buttonStyle: string;
  /** What the button does; a button without an action is disabled. */
  onClick?: () => void;
  /** Bold line under the price (the plan's credits) and a short description below it. */
  headline?: string;
  tagline?: string;
  features: string[];
  /** Coloured light at the top of the card. */
  glow: string;
  isHighlighted?: boolean;
  /** White card with dark text (Enterprise). */
  light?: boolean;
  isSubscribed: boolean;
}

const DATE_FORMAT = 'MMMM DD, YYYY';

const BUTTON = {
  primary: 'bg-green01 text-black hover:bg-green02 shadow-[0_10px_30px_-12px_rgba(179,236,73,0.8)]',
  dark: 'bg-black text-white hover:bg-black/85',
  light: 'bg-white text-black hover:bg-gray-100',
  secondary: 'border border-white/15 bg-white/[0.08] text-white hover:bg-white/[0.14]',
  subscribed: 'border border-white/10 bg-white/[0.06] text-white/60 cursor-not-allowed',
};
const SUBSCRIBED_BUTTON = BUTTON.subscribed;

const GLOW = {
  trial: 'radial-gradient(120% 70% at 50% -10%, rgba(255,255,255,0.12), transparent 60%)',
  free: 'radial-gradient(110% 75% at 30% -15%, rgba(34,197,94,0.32), transparent 62%)',
  professional: [
    'radial-gradient(135% 105% at 55% -25%, rgba(179,236,73,0.70), rgba(132,204,22,0.28) 48%, transparent 82%)',
    'linear-gradient(180deg, rgba(179,236,73,0.16), transparent 70%)',
  ].join(', '),
  ultimate: [
    'radial-gradient(110% 80% at 75% -20%, rgba(56,189,248,0.38), rgba(45,212,191,0.14) 45%, transparent 70%)',
    'linear-gradient(180deg, rgba(56,189,248,0.06), transparent 50%)',
  ].join(', '),
  // On the white Enterprise card.
  enterprise: [
    'radial-gradient(120% 80% at 85% -20%, rgba(179,236,73,0.55), transparent 60%)',
    'radial-gradient(90% 70% at 0% 110%, rgba(45,212,191,0.22), transparent 65%)',
    'linear-gradient(160deg, #ffffff 0%, #f4f7ef 100%)',
  ].join(', '),
  team: 'radial-gradient(90% 90% at 50% -20%, rgba(45,212,191,0.28), rgba(34,197,94,0.10) 45%, transparent 75%)',
};

type Audience = 'individual' | 'team';
type BillingPeriod = 'monthly' | 'yearly';

/** Yearly billing discount; the monthly equivalent is rounded down to a whole amount. */
const YEARLY_DISCOUNT = 0.1;

/** Monthly equivalent of a yearly subscription, in cents, rounded down to whole currency units. */
const yearlyMonthlyAmount = (monthlyAmount: number) => Math.floor((monthlyAmount * (1 - YEARLY_DISCOUNT)) / 100) * 100;

/** Enterprise is not sold online: the button writes to sales. */
const SALES_MAILTO = 'mailto:info@taman.tech?subject=Taman%20Enterprise';
const CUSTOM_CREDITS_MAILTO = 'mailto:info@taman.tech?subject=Taman%20custom%20credits';

/** What each plan includes, as keys under `settings.plans.feature`. */
const PLAN_FEATURES: Record<'FREE' | PaidTier, string[]> = {
  FREE: ['workspace3d', 'sampleProjects', 'drawingTools', 'projectPreview', 'trialOptimization', 'photometricViewer'],
  PROFESSIONAL: [
    'everythingInFree',
    'planImport',
    'lightingOptimization',
    'marketMatch',
    'projectVersions',
    'pdfReports',
  ],
  ULTIMATE: [
    'everythingInProfessional',
    'monthlyCredits4x',
    'optimizationCapacity',
    'designAlternatives',
    'creditTopUps',
    'usageOverview',
  ],
};

const ENTERPRISE_FEATURES = [
  'everythingInUltimate',
  'customCredits',
  'teamSeats',
  'onboarding',
  'prioritySupport',
  'invoicing',
];

export const PlansSubscriptions: React.FC<{ isModal?: boolean }> = ({ isModal }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const subscription = useAppSelector(getSubscription);
  const isLoadingSubscription = useAppSelector(getIsLoadingSubscription);
  const subscriptionError = useAppSelector(getSubscriptionError);
  const paymentsEnabled = useAppSelector((state) => getUserFeatureFlags(state).includes(ONLINE_PAYMENTS_FLAG));
  const catalog = useAppSelector(getBillingCatalog);
  const isOpeningPaymentPage = useAppSelector(getIsOpeningPaymentPage);
  const isSwitchingToFree = useAppSelector(getIsSwitchingToFree);
  const [confirmingSwitchToFree, setConfirmingSwitchToFree] = useState(false);
  const [audience, setAudience] = useState<Audience>('individual');
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const yearly = billingPeriod === 'yearly';

  useEffect(() => {
    if (!subscription && !isLoadingSubscription && !subscriptionError) {
      dispatch(loadSubscription());
    }
  }, [dispatch, subscription, isLoadingSubscription, subscriptionError]);

  useEffect(() => {
    if (paymentsEnabled) {
      dispatch(loadBillingCatalog());
    }
  }, [dispatch, paymentsEnabled]);

  const currentTier: SubscriptionTier = subscription?.tier || 'FREE';
  // A subscription paid through the payment provider is changed on the provider's portal.
  const isProviderManaged = !!subscription?.providerManaged;

  const isOnPaidPlan = currentTier === 'PROFESSIONAL' || currentTier === 'ULTIMATE';
  const isPastDue = isProviderManaged && subscription?.paymentStatus === 'PAST_DUE';

  const trialExpiryText =
    currentTier === 'TRIAL' && subscription?.startedAt
      ? t('settings.plans.trialExpiresOn', {
          date: moment(subscription.startedAt).add(14, 'days').format(DATE_FORMAT),
        })
      : undefined;

  /** Plan cards while online payments are off: paid plans are a blurred preview. */
  const previewPlans: PlanCard[] = [
    {
      name: t('settings.plans.trial'),
      tier: 'TRIAL',
      price: '€0',
      period: t('settings.plans.perYear'),
      freeText: trialExpiryText ?? t('settings.plans.freeCredits', { count: 10 }),
      buttonText: currentTier === 'TRIAL' ? t('settings.plans.subscribed') : t('settings.plans.comingSoon'),
      buttonStyle: currentTier === 'TRIAL' ? SUBSCRIBED_BUTTON : BUTTON.light,
      features: [t('settings.plans.freeCredits', { count: 2000 })],
      glow: GLOW.trial,
      isSubscribed: currentTier === 'TRIAL',
    },
    {
      name: t('settings.plans.free'),
      tier: 'FREE',
      price: '€0',
      period: t('settings.plans.perYear'),
      freeText: t('settings.plans.freeUntilCreditsRunOut'),
      buttonText: currentTier === 'FREE' ? t('settings.plans.subscribed') : t('settings.plans.switchToFree'),
      buttonStyle: currentTier === 'FREE' ? SUBSCRIBED_BUTTON : BUTTON.light,
      features: [t('settings.plans.freeCredits', { count: 200 })],
      glow: GLOW.free,
      isSubscribed: currentTier === 'FREE',
    },
    {
      name: t('settings.plans.professional'),
      tier: 'PROFESSIONAL',
      price: '€xx',
      originalPrice: '€xx',
      period: t('settings.plans.perMonthBilledAnnually'),
      freeText: t('settings.plans.freeThrough', { date: 'xxxx xx, xxxx' }),
      buttonText: currentTier === 'PROFESSIONAL' ? t('settings.plans.subscribed') : t('settings.plans.comingSoon'),
      buttonStyle: currentTier === 'PROFESSIONAL' ? SUBSCRIBED_BUTTON : BUTTON.primary,
      features: [
        t('settings.plans.creditsPerMonth', { count: 0 }),
        t('settings.plans.projects', { count: 0 }),
        t('settings.plans.scenes', { count: 0 }),
        t('settings.plans.shotsPerProject', { count: 0 }),
        t('settings.plans.plusEverythingInFreeTier'),
      ],
      glow: GLOW.professional,
      isHighlighted: true,
      isSubscribed: currentTier === 'PROFESSIONAL',
    },
    {
      name: t('settings.plans.ultimate'),
      tier: 'ULTIMATE',
      price: '€xx',
      originalPrice: '€xx',
      period: t('settings.plans.perMonthBilledAnnually'),
      freeText: t('settings.plans.freeThrough', { date: 'xxxx xx, xxxx' }),
      buttonText: currentTier === 'ULTIMATE' ? t('settings.plans.subscribed') : t('settings.plans.comingSoon'),
      buttonStyle: currentTier === 'ULTIMATE' ? SUBSCRIBED_BUTTON : BUTTON.secondary,
      features: [
        t('settings.plans.creditsPerMonth', { count: 0 }),
        t('settings.plans.projects', { count: 0 }),
        t('settings.plans.scenes', { count: 0 }),
        t('settings.plans.shots', { count: 0 }),
        t('settings.plans.plusEverythingInIndividualTier'),
      ],
      glow: GLOW.ultimate,
      isSubscribed: currentTier === 'ULTIMATE',
    },
  ];

  /** State of the user's paid subscription, shown on the card of the current plan. */
  const paidSubscriptionText = (): string => {
    if (!subscription?.providerManaged || !subscription.currentPeriodEnd) return '';
    if (subscription.paymentStatus === 'PAST_DUE') return t('settings.plans.paymentFailed');
    const date = moment(subscription.currentPeriodEnd).format(DATE_FORMAT);
    return subscription.cancelAtPeriodEnd
      ? t('settings.plans.endsOn', { date })
      : t('settings.plans.renewsOn', { date });
  };

  const paidPlan = (tier: PaidTier, look: Pick<PlanCard, 'name' | 'glow' | 'isHighlighted'>) => {
    const offer = catalog?.plans.find((plan) => plan.tier === tier);
    const isCurrent = currentTier === tier;
    const actionStyle = look.isHighlighted ? BUTTON.primary : BUTTON.secondary;

    let button: Pick<PlanCard, 'buttonText' | 'buttonStyle' | 'onClick'>;
    if (isCurrent && isProviderManaged) {
      button = {
        buttonText: isPastDue ? t('settings.plans.updatePaymentMethod') : t('settings.plans.manageSubscription'),
        buttonStyle: actionStyle,
        onClick: () => dispatch(openBillingPortal()),
      };
    } else if (isCurrent) {
      // Assigned by Taman, not bought: there is nothing to manage.
      button = { buttonText: t('settings.plans.subscribed'), buttonStyle: SUBSCRIBED_BUTTON };
    } else if (isProviderManaged) {
      button = {
        // While a payment is overdue the provider only lets the user fix the payment method; a plan
        // change becomes possible once it is paid.
        buttonText: isPastDue ? t('settings.plans.updatePaymentMethod') : t('settings.plans.switchPlan'),
        buttonStyle: actionStyle,
        onClick: () => dispatch(openBillingPortal()),
      };
    } else if (offer && yearly) {
      // TODO(backend): yearly prices at the payment provider and in the catalogue; until then
      // yearly billing is shown but cannot be bought.
      button = { buttonText: t('settings.plans.comingSoon'), buttonStyle: SUBSCRIBED_BUTTON };
    } else if (offer) {
      button = {
        buttonText: t('settings.plans.subscribe'),
        buttonStyle: actionStyle,
        onClick: () => dispatch(startSubscriptionCheckout(tier)),
      };
    } else {
      button = { buttonText: t('settings.plans.comingSoon'), buttonStyle: SUBSCRIBED_BUTTON };
    }

    const card: PlanCard = {
      ...look,
      ...button,
      tier,
      price: offer
        ? formatPrice(yearly ? yearlyMonthlyAmount(offer.amount) : offer.amount, offer.currency, i18n.language)
        : '—',
      // Tax depends on where the customer is and is added at checkout.
      // Yearly: a short period next to the price; the yearly total goes in the note below.
      period: yearly ? t('settings.plans.perMonth') : t('settings.plans.perMonthPlusTaxes'),
      priceNote:
        offer && yearly
          ? t('settings.plans.billedYearly', {
              price: formatPrice(yearlyMonthlyAmount(offer.amount) * 12, offer.currency, i18n.language),
            })
          : undefined,
      freeText: isCurrent ? paidSubscriptionText() : '',
      headline: offer
        ? t('settings.plans.creditsMonthly', {
            credits: new Intl.NumberFormat(i18n.language).format(offer.monthlyCredits),
          })
        : undefined,
      tagline: t(`settings.plans.tagline.${tier.toLowerCase()}`),
      features: PLAN_FEATURES[tier].map((key) => t(`settings.plans.feature.${key}`)),
      isSubscribed: isCurrent,
    };
    return card;
  };

  /** Plan cards with online payments: real prices, and buttons that buy or manage. */
  const purchasablePlans: PlanCard[] = [
    // The trial cannot be chosen; it is only shown while the user is on it.
    ...(currentTier === 'TRIAL' ? [{ ...previewPlans[0], buttonText: t('settings.plans.subscribed') }] : []),
    {
      ...previewPlans[1],
      period: t('settings.plans.perMonth'),
      freeText: '',
      headline: t('settings.plans.trialCreditsHeadline'),
      tagline: t('settings.plans.tagline.free'),
      features: PLAN_FEATURES.FREE.map((key) => t(`settings.plans.feature.${key}`)),
      buttonText:
        currentTier === 'FREE'
          ? t('settings.plans.subscribed')
          : isProviderManaged || isOnPaidPlan
            ? t('settings.plans.switchToFree')
            : undefined,
      // A paid subscription is cancelled on the portal, where it runs until the end of the paid
      // period. A plan nobody pays for is given up right here, after asking.
      onClick: isProviderManaged
        ? () => dispatch(openBillingPortal())
        : isOnPaidPlan
          ? () => setConfirmingSwitchToFree(true)
          : undefined,
    },
    paidPlan('PROFESSIONAL', {
      name: t('settings.plans.professional'),
      glow: GLOW.professional,
      isHighlighted: true,
    }),
    paidPlan('ULTIMATE', {
      name: t('settings.plans.ultimate'),
      glow: GLOW.ultimate,
    }),
  ];

  const enterprisePlan: PlanCard = {
    name: t('settings.plans.enterprise'),
    tier: 'ENTERPRISE',
    price: t('settings.plans.customPrice'),
    period: '',
    freeText: '',
    headline: t('settings.plans.enterpriseHeadline'),
    tagline: t('settings.plans.tagline.enterprise'),
    features: ENTERPRISE_FEATURES.map((key) => t(`settings.plans.feature.${key}`)),
    buttonText: t('settings.plans.contactSales'),
    buttonStyle: BUTTON.dark,
    onClick: () => {
      window.location.href = SALES_MAILTO;
    },
    glow: GLOW.enterprise,
    light: true,
    isSubscribed: false,
  };

  const plans = paymentsEnabled ? [...purchasablePlans, enterprisePlan] : previewPlans;
  const gridColumns = plans.length <= 3 ? 'lg:grid-cols-3' : plans.length === 4 ? 'xl:grid-cols-4' : 'xl:grid-cols-5';
  // Credits are of no use on the Free plan, which cannot start calculations.
  const creditPacks = paymentsEnabled && currentTier !== 'FREE' ? (catalog?.creditPacks ?? []) : [];

  // Only while there is nothing to show yet: a refresh in the background must not blank the plans.
  if (isLoadingSubscription && !subscription) {
    return (
      <div
        className={`flex-1 overflow-auto p-4 md:p-8 bg-black flex items-center justify-center ${isModal ? 'h-full' : ''}`}
      >
        <div className="text-white">{t('settings.plans.loadingSubscription')}</div>
      </div>
    );
  }

  if (subscriptionError) {
    return (
      <div
        className={`flex-1 overflow-auto p-4 md:p-8 bg-black flex items-center justify-center ${isModal ? 'h-full' : ''}`}
      >
        <div className="text-red-500">{t(subscriptionError)}</div>
      </div>
    );
  }

  const busy = isOpeningPaymentPage || isSwitchingToFree;

  return (
    <div
      className={`flex-1 overflow-auto px-4 md:px-6 xl:px-8 ${isModal ? 'h-full py-4 short:py-3' : 'bg-black py-5 short:py-3'}`}
    >
      <div className="mx-auto max-w-[1180px]">
        {/* Individual / Team, and the billing period. Without online payments only the plan preview shows. */}
        {paymentsEnabled && (
          <div className="mb-5 flex flex-col items-center justify-center gap-3 sm:flex-row short:mb-4">
            <div
              role="tablist"
              aria-label={t('settings.plans.audience')}
              className="inline-flex h-11 w-full max-w-xs rounded-full border border-white/10 bg-white/[0.04] p-1 sm:w-auto"
            >
              {(['individual', 'team'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={audience === value}
                  onClick={() => setAudience(value)}
                  className={`flex flex-1 items-center justify-center rounded-full px-8 text-sm font-medium transition-all duration-200 sm:flex-none sm:px-10 ${
                    audience === value
                      ? 'bg-green01 text-black shadow-[0_0_24px_-4px_rgba(179,236,73,0.55)]'
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  {t(`settings.plans.${value}`)}
                </button>
              ))}
            </div>
            {/* Hidden, not removed, on the Team tab: the toggles stay where they are. */}
            <div
              role="radiogroup"
              aria-label={t('settings.plans.billingPeriod')}
              aria-hidden={audience === 'team'}
              className={`inline-flex h-11 w-full max-w-xs rounded-full border border-white/10 bg-white/[0.04] p-1 sm:w-auto ${
                audience === 'team' ? 'invisible' : ''
              }`}
            >
              {(['monthly', 'yearly'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={billingPeriod === value}
                  onClick={() => setBillingPeriod(value)}
                  className={`flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-full px-3 text-sm font-medium transition-all duration-200 sm:flex-none sm:px-5 ${
                    billingPeriod === value ? 'bg-white text-black' : 'text-white/70 hover:text-white'
                  }`}
                >
                  {t(`settings.plans.${value}`)}
                  {value === 'yearly' && (
                    <span
                      className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        billingPeriod === value ? 'bg-green01 text-black' : 'bg-green01/15 text-green01'
                      }`}
                    >
                      {t('settings.plans.yearlyDiscount', { percent: Math.round(YEARLY_DISCOUNT * 100) })}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Both tabs share one grid cell, so switching never moves anything on the page. */}
        <div className="grid">
          <div
            aria-hidden={audience === 'team'}
            className={`[grid-area:1/1] ${audience === 'team' ? 'invisible' : ''}`}
          >
            <div className={`grid grid-cols-1 gap-4 md:grid-cols-2 ${gridColumns}`}>
              {plans.map((plan) => {
                // Without online payments the plans the user is not on are only a preview.
                const blur = !paymentsEnabled && !plan.isSubscribed && plan.tier !== 'ENTERPRISE' ? 'blur-sm' : '';
                const ink = plan.light
                  ? {
                      title: 'text-black',
                      soft: 'text-black/55',
                      body: 'text-black/80',
                      line: 'bg-black/10',
                      check: 'text-green04',
                    }
                  : {
                      title: 'text-white',
                      soft: 'text-white/55',
                      body: 'text-white/85',
                      line: 'bg-white/10',
                      check: 'text-green01',
                    };
                return (
                  <div
                    key={plan.tier}
                    className={`relative flex flex-col rounded-2xl border p-5 2xl:p-6 short:p-4 ${
                      plan.light ? 'border-white bg-white' : 'bg-[#0c0e0d]'
                    } ${
                      plan.isHighlighted
                        ? 'border-green01 shadow-[0_0_0_1px_rgba(179,236,73,0.35),0_30px_80px_-30px_rgba(179,236,73,0.85)]'
                        : plan.light
                          ? 'shadow-[0_24px_60px_-30px_rgba(255,255,255,0.35)]'
                          : 'border-white/10'
                    }`}
                    data-testid={`plan-card-${plan.tier}`}
                  >
                    <div
                      className="pointer-events-none absolute inset-0 rounded-[inherit]"
                      style={{ background: plan.glow }}
                    />
                    {/* On the card's top edge, so it never competes with the plan name for width. */}
                    {plan.isHighlighted && (
                      <span className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-green01 px-3 py-1 text-xs font-semibold text-black shadow-[0_0_20px_-4px_rgba(179,236,73,0.7)]">
                        {t('settings.plans.recommended')}
                      </span>
                    )}

                    <div className="relative flex flex-1 flex-col">
                      <h2 className={`text-xl font-medium xl:text-2xl ${ink.title}`}>{plan.name}</h2>

                      <div className={`mt-3 flex items-baseline gap-x-2 short:mt-2 ${blur}`}>
                        <span
                          className={`text-[38px] font-semibold leading-none tracking-tight xl:text-[34px] 2xl:text-[40px] short:text-[32px] ${ink.title}`}
                        >
                          {plan.price}
                        </span>
                        {plan.originalPrice && <span className="text-2xl text-white/40">/{plan.originalPrice}</span>}
                      </div>
                      {/* One line of fixed height in every card and billing period, so switching never shifts the cards. */}
                      <p className={`mt-1.5 h-5 truncate text-sm leading-5 ${ink.soft} ${blur}`}>
                        {[plan.period, plan.priceNote].filter(Boolean).join(' · ')}
                      </p>
                      {plan.headline && (
                        <p className={`mt-3 text-base font-semibold short:mt-2 ${ink.title} ${blur}`}>
                          {plan.headline}
                        </p>
                      )}
                      {plan.tagline && <p className={`text-sm ${ink.soft} ${blur}`}>{plan.tagline}</p>}
                      {plan.freeText && <p className={`mt-2 text-xs text-green01/90 ${blur}`}>{plan.freeText}</p>}

                      {plan.features.length > 0 && (
                        <>
                          <div className={`my-4 h-px short:my-3 ${ink.line}`} />
                          <ul className={`space-y-2 short:space-y-1.5 ${isModal ? 'space-y-1.5' : ''}`}>
                            {plan.features.map((feature, featureIndex) => (
                              <li key={featureIndex} className="flex items-start gap-3">
                                <Check className={`mt-0.5 h-4 w-4 flex-shrink-0 ${ink.check}`} strokeWidth={2.5} />
                                <span className={`text-sm ${ink.body} ${blur}`}>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}

                      <div className="mt-auto pt-4 short:pt-3">
                        {plan.buttonText ? (
                          <button
                            className={`h-11 w-full rounded-xl text-sm font-semibold transition-colors duration-200 disabled:cursor-not-allowed short:h-10 ${plan.buttonStyle} ${blur}`}
                            disabled={!plan.onClick || busy}
                            onClick={plan.onClick}
                          >
                            {plan.buttonText}
                          </button>
                        ) : (
                          <div className="h-11 short:h-10" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {creditPacks.length > 0 && (
              <section
                className="mt-4 flex flex-col gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4 lg:flex-row lg:items-center lg:justify-between lg:px-6 lg:py-4 short:mt-3 short:py-3"
                data-testid="credit-packs"
              >
                <div className="lg:max-w-xl">
                  <h2 className="text-lg font-medium text-white">{t('settings.plans.creditPacksTitle')}</h2>
                  <p className="mt-1 text-sm text-white/60">{t('settings.plans.creditPacksDescription')}</p>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col gap-3 sm:flex-row">
                    {creditPacks.map((pack) => (
                      <button
                        key={pack.key}
                        aria-label={`${t('settings.plans.buyCreditPack')} ${t('settings.plans.creditPackCredits', { count: pack.credits })}`}
                        className="h-11 min-w-[150px] whitespace-nowrap rounded-xl border border-white/20 px-5 short:h-10 text-sm font-medium text-white transition-colors duration-200 hover:border-green01 hover:text-green01 disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={isOpeningPaymentPage}
                        onClick={() => dispatch(startCreditPackCheckout(pack.key))}
                      >
                        {t('settings.plans.creditPackCredits', { count: pack.credits })} ·{' '}
                        {formatPrice(pack.amount, pack.currency, i18n.language)}
                      </button>
                    ))}
                    <a
                      href={CUSTOM_CREDITS_MAILTO}
                      aria-label={t('settings.plans.customCreditsLabel')}
                      className="flex h-11 min-w-[120px] items-center justify-center whitespace-nowrap rounded-xl border border-dashed border-white/25 px-4 text-sm font-medium text-white/80 transition-colors duration-200 hover:border-green01 hover:text-green01 short:h-10"
                    >
                      {t('settings.plans.customPrice')}
                    </a>
                  </div>
                  <p className="text-right text-[11px] leading-none text-white/45">{t('settings.plans.plusTaxes')}</p>
                </div>
              </section>
            )}
          </div>
          {paymentsEnabled && (
            <div
              aria-hidden={audience === 'individual'}
              className={`[grid-area:1/1] ${audience === 'individual' ? 'invisible' : ''}`}
            >
              <div className="relative mx-auto max-w-xl overflow-hidden rounded-2xl border border-white/10 bg-[#0c0e0d] p-8 text-center">
                <div className="pointer-events-none absolute inset-0" style={{ background: GLOW.team }} />
                <div className="relative">
                  <h2 className="text-2xl font-medium text-white">{t('settings.plans.teamPlans')}</h2>
                  <p className="mt-2 text-sm text-white/60">{t('settings.plans.comingSoon')}</p>
                  <a
                    href={SALES_MAILTO}
                    className={`mt-6 inline-flex h-11 items-center justify-center rounded-xl px-8 text-sm font-semibold transition-colors duration-200 ${BUTTON.light}`}
                  >
                    {t('settings.plans.contactSales')}
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

        {confirmingSwitchToFree && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[1400] flex items-center justify-center p-4"
            onClick={() => setConfirmingSwitchToFree(false)}
            data-testid="switch-to-free-confirmation"
          >
            <div
              className="bg-greyP5 rounded-md shadow-xl w-[602px] max-w-full p-10"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-white text-lg font-medium pb-2 border-b border-greyP4">
                {t('settings.plans.switchToFreeConfirmTitle')}
              </h2>
              <p className="text-white pt-8">{t('settings.plans.switchToFreeConfirmText')}</p>
              <div className="flex items-center justify-end space-x-4 pt-8">
                <button
                  className="px-6 py-3 rounded-full font-medium text-xs border border-gray-600 text-white hover:bg-greyP4 transition-colors duration-200"
                  onClick={() => setConfirmingSwitchToFree(false)}
                >
                  {t('common.cancel')}
                </button>
                <button
                  className="px-6 py-3 rounded-full font-medium text-xs bg-green01 text-black hover:bg-green02 transition-colors duration-200"
                  onClick={() => {
                    setConfirmingSwitchToFree(false);
                    dispatch(switchToFree());
                  }}
                >
                  {t('settings.plans.switchToFree')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
